import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { Col, Flex, Row, Tooltip } from 'antd';
import { Info } from '@phosphor-icons/react';
import { countryListHook } from '../../../hooks';
import { MuteText, ShadowBox } from '../../../elements';
import {
  ControlledCountrySelect,
  ControlledSwitch,
  ControlledInput,
  ControlledNumberInput,
  ControlledPhoneInput,
  ControlledSelect,
} from '@elements/controlled-fields';
import { CommonUtility, measurementOptions, ToastMessage } from '../../../utility';
import { LoaderBar } from '../../../components';
import { AlertFooterBar } from '../../../elements/FooterAlert';
import { WarehouseService } from '../../../utility/services/warehouse';
import { useWarehouseDetails } from '../../../screens/app/settings/warehouse';
import { IconSizes } from '@utility/constants/style-contants';

const measurementSelectOptions = measurementOptions.map(option => ({
  label: option.text,
  value: option.value,
}));

function SettingLabelWithTooltip({ label, tooltip }) {
  return (
    <span className="d-flex align-items-center">
      <MuteText>{label}</MuteText>
      <Tooltip title={tooltip}>
        <Info className="ml-1" size={IconSizes.small} />
      </Tooltip>
    </span>
  );
}

const WarehouseSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup.string().required('Email is required').email('Enter a valid Email'),
  phone: yup.string(),
  addressLineOne: yup.string().required('Address Line 1 is required'),
  addressLineTwo: yup.string(),
  city: yup.string().required('City is required'),
  countryCode: yup.string().required('Country is required'),
  state: yup.string().required('State is required'),
  zipCode: yup.string().required('Zip Code is required'),
  showBarcodeOnPacking: yup.boolean(),
  deferNonPickLocationAllocationsFromAutoWave: yup.boolean(),
  pickableLocations: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null)),
  nonPickableLocations: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null)),
  length: yup
    .number()
    .positive('Length must be greater than 0')
    .typeError('Length is required')
    .required('Length is required'),
  width: yup
    .number()
    .positive('Width must be greater than 0')
    .typeError('Width is required')
    .required('Width is required'),
  height: yup
    .number()
    .positive('Height must be greater than 0')
    .typeError('Height is required')
    .required('Height is required'),
  measurementUnit: yup.string().required('Measurement Unit is required'),
});

export function WarehouseForm() {
  const { warehouseData, warehouseDataLoading, refreshWarehouse, warehouseId } =
    useWarehouseDetails();
  const { data: countryData } = countryListHook();
  const [saving, setSaving] = useState(false);

  const countryOptions = useMemo(
    () =>
      (countryData || []).map(opt => ({
        value: opt.value,
        label: opt.text,
        flag: String(opt.value || '').toLowerCase(),
      })),
    [countryData],
  );

  const {
    formState: { dirtyFields },
    handleSubmit,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(WarehouseSchema),
    defaultValues: {
      showBarcodeOnPacking: false,
      deferNonPickLocationAllocationsFromAutoWave: false,
    },
  });

  useEffect(() => {
    if (!CommonUtility.isValidObject(warehouseData)) return;
    resetForm(warehouseData);
  }, [warehouseData]);

  const resetForm = data => {
    reset({
      ...data.address,
      ...data.dimensions,
      ...(data?.shipnodeConfigurations || {}),
      ...(data?.thresholds || {}),
      ...(data?.packingSettings || {}),
      ...(data?.fulfillmentSettings || {}),
      deferNonPickLocationAllocationsFromAutoWave:
        data?.fulfillmentSettings?.deferNonPickLocationAllocationsFromAutoWave ?? false,
      name: data?.name,
      email: data?.email,
      phone: data?.phone,
    });
  };

  const onSubmit = async formData => {
    try {
      setSaving(true);

      let payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          addressLineOne: formData.addressLineOne,
          addressLineTwo: formData.addressLineTwo,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          countryCode: formData.countryCode,
        },
        dimensions: {
          length: formData.length,
          width: formData.width,
          height: formData.height,
          measurementUnit: formData.measurementUnit,
        },
        thresholds: {
          pickableLocations: formData.pickableLocations,
          nonPickableLocations: formData.nonPickableLocations,
        },
        packingSettings: {
          showBarcodeOnPacking: formData.showBarcodeOnPacking,
        },
        fulfillmentSettings: {
          deferNonPickLocationAllocationsFromAutoWave:
            formData.deferNonPickLocationAllocationsFromAutoWave ?? false,
        },
      };

      payload = CommonUtility.removeEmptyFields(payload);

      const res = await WarehouseService.updateWarehouse(warehouseId, payload);
      ToastMessage.success('Warehouse updated successfully');
      resetForm(res);
      refreshWarehouse();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div>
        {warehouseDataLoading && <LoaderBar />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <ShadowBox className="mb-4">
            <h3>Re-Stock Alert Thresholds</h3>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <ControlledNumberInput
                  control={control}
                  name="pickableLocations"
                  label="Pickable Locations"
                  placeholder="0"
                />
              </Col>
              <Col xs={24} md={12}>
                <ControlledNumberInput
                  control={control}
                  name="nonPickableLocations"
                  label="Non-Pickable Locations"
                  placeholder="0"
                />
              </Col>
            </Row>
          </ShadowBox>
          <ShadowBox>
            <h3>Warehouse Details</h3>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  name="name"
                  label="Name"
                  placeholder="Warehouse name"
                  required
                />
              </Col>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  name="email"
                  label="E-mail"
                  placeholder="name@company.com"
                  required
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <ControlledPhoneInput
                  control={control}
                  name="phone"
                  label="Phone"
                  placeholder="Phone number"
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <ControlledSwitch
                  control={control}
                  layout="horizontal"
                  name="showBarcodeOnPacking"
                  label={
                    <SettingLabelWithTooltip
                      label="Display barcode during packing"
                      tooltip="When enabled, a popup displaying the barcode will appear during the packing process once all items have been packed, allowing it to be scanned."
                    />
                  }
                />
                <ControlledSwitch
                  control={control}
                  layout="horizontal"
                  name="deferNonPickLocationAllocationsFromAutoWave"
                  label={
                    <SettingLabelWithTooltip
                      label="Defer non-pick shelf allocations from automatic waves"
                      tooltip="When enabled, only pick-shelf stock is automatically queued to a wave after allocation. Storage or non-pick shelf lines stay unassigned to a wave until you add them."
                    />
                  }
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={8} md={6}>
                <ControlledNumberInput
                  control={control}
                  name="length"
                  label="Length"
                  placeholder="0.00"
                  required
                  min={0}
                  step={0.01}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <ControlledNumberInput
                  control={control}
                  name="width"
                  label="Width"
                  placeholder="0.00"
                  required
                  min={0}
                  step={0.01}
                />
              </Col>
              <Col xs={24} sm={8} md={6}>
                <ControlledNumberInput
                  control={control}
                  name="height"
                  label="Height"
                  placeholder="0.00"
                  required
                  min={0}
                  step={0.01}
                />
              </Col>
              <Col xs={24} md={6}>
                <ControlledSelect
                  control={control}
                  label="Measurement Unit"
                  name="measurementUnit"
                  placeholder="Select unit"
                  required
                  options={measurementSelectOptions}
                />
              </Col>
            </Row>
          </ShadowBox>
          <ShadowBox className="my-3">
            <h3>Address</h3>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  label="Address Line 1"
                  name="addressLineOne"
                  placeholder="Street address"
                  required
                />
              </Col>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  label="Address Line 2"
                  name="addressLineTwo"
                  placeholder="Apartment, suite, etc."
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  name="city"
                  label="City"
                  placeholder="City"
                  required
                />
              </Col>
              <Col xs={24} md={12}>
                <ControlledCountrySelect
                  control={control}
                  name="countryCode"
                  label="Country"
                  placeholder="Select country"
                  required
                  options={countryOptions}
                  showSearch
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  name="state"
                  label="State"
                  placeholder="State"
                  required
                />
              </Col>
              <Col xs={24} md={12}>
                <ControlledInput
                  control={control}
                  name="zipCode"
                  label="Zip Code"
                  placeholder="Zip code"
                  required
                />
              </Col>
            </Row>
          </ShadowBox>
          {CommonUtility.isValidObject(dirtyFields) && (
            <AlertFooterBar
              message="Save Changes"
              cancel={() => resetForm(warehouseData)}
              save={handleSubmit(onSubmit)}
              loading={saving}
            />
          )}
        </form>
      </div>
    </>
  );
}
